import React, { useRef, useState } from 'react';
import { AnyPinContent, Icon, ColorPalette, CarouselPinContent, StandardPinContent, ListiclePinContent, ThemeCollagePinContent, AspectRatio, TipPinContent, SmartCarouselPinContent } from '../types';
import Loader from './ui/Loader';
import { API_BASE } from '../apiBase';

type ExportFormat = 'png' | 'jpg' | 'webp';
type ExportSize = 'pinterest' | '1:1' | 'original';

const EXPORT_SIZES: Record<ExportSize, { width: number; height: number; label: string }> = {
    'pinterest': { width: 1000, height: 1500, label: 'Pinterest (1000×1500)' },
    '1:1': { width: 1080, height: 1080, label: 'Square (1080×1080)' },
    'original': { width: 0, height: 0, label: 'Original Size' }
};

const getAspectRatioClass = (ratio: AspectRatio) => {
    switch (ratio) {
        case '1:1': return 'aspect-square';
        case '4:5': return 'aspect-[4/5]';
        case '4:3': return 'aspect-[4/3]';
        case '2:3':
        default:
            return 'aspect-[2/3]';
    }
};

interface ExportPanelProps {
    containerRef: React.RefObject<HTMLDivElement>;
    filename?: string;
}

const ExportPanel: React.FC<ExportPanelProps> = ({ containerRef, filename = 'pin-export' }) => {
    const [format, setFormat] = useState<ExportFormat>('png');
    const [size, setSize] = useState<ExportSize>('pinterest');
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const exportPin = async () => {
        if (!containerRef.current) {
            setError('No content to export');
            return;
        }
        setIsExporting(true);
        setError(null);

        try {
            const html2canvas = (await import('html2canvas')).default;

            // Find the actual pin content element inside the container
            const pinElement = containerRef.current.querySelector('[data-pin-content]') || containerRef.current;

            console.log('Exporting element:', pinElement);

            const canvas = await html2canvas(pinElement as HTMLElement, {
                scale: 3, // Higher quality
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: true, // Enable logging for debugging
                imageTimeout: 15000, // Longer timeout for images
                onclone: (clonedDoc: Document) => {
                    const images = clonedDoc.querySelectorAll('img');
                    images.forEach((img: HTMLImageElement) => {
                        if (img.src.startsWith('data:')) {
                            img.crossOrigin = 'anonymous';
                        }
                    });
                }
            });

            if (canvas.width === 0 || canvas.height === 0) {
                throw new Error('Canvas is empty');
            }

            let finalCanvas = canvas;
            const sizeConfig = EXPORT_SIZES[size];

            if (size !== 'original' && sizeConfig.width > 0) {
                finalCanvas = document.createElement('canvas');
                finalCanvas.width = sizeConfig.width;
                finalCanvas.height = sizeConfig.height;
                const ctx = finalCanvas.getContext('2d');
                if (ctx) {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, sizeConfig.width, sizeConfig.height);

                    const scale = Math.min(
                        sizeConfig.width / canvas.width,
                        sizeConfig.height / canvas.height
                    );
                    const x = (sizeConfig.width - canvas.width * scale) / 2;
                    const y = (sizeConfig.height - canvas.height * scale) / 2;
                    ctx.drawImage(canvas, x, y, canvas.width * scale, canvas.height * scale);
                }
            }

            const mimeType = format === 'jpg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : 'image/png';
            const quality = format === 'png' ? 1 : 0.95;

            // Use data URL instead of blob for reliable cross-browser download
            const dataUrl = finalCanvas.toDataURL(mimeType, quality);
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${filename}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            console.log('Export successful:', filename, format);
            setIsExporting(false);
        } catch (err) {
            console.error('Export failed:', err);
            setError(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setIsExporting(false);
        }
    };

    return (
        <div className="mt-4 p-4 bg-slate-100 rounded-xl border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-700 mb-3">📥 Export Options</h4>

            <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                    <label className="text-xs text-slate-500 mb-1 block">Format</label>
                    <div className="flex gap-1">
                        {(['png', 'jpg', 'webp'] as ExportFormat[]).map(f => (
                            <button
                                key={f}
                                onClick={() => setFormat(f)}
                                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${format === f
                                    ? 'bg-pink-600 text-white'
                                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                                    }`}
                            >
                                {f.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs text-slate-500 mb-1 block">Size</label>
                    <select
                        value={size}
                        onChange={e => setSize(e.target.value as ExportSize)}
                        className="w-full py-1.5 px-2 text-xs bg-white border border-slate-200 rounded-md text-slate-700"
                    >
                        {Object.entries(EXPORT_SIZES).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <button
                onClick={exportPin}
                disabled={isExporting}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 disabled:opacity-50 text-white font-bold py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
            >
                {isExporting ? (
                    <>
                        <Loader />
                        <span>Exporting...</span>
                    </>
                ) : (
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                        </svg>
                        <span>Download {format.toUpperCase()}</span>
                    </>
                )}
            </button>
            {error && (
                <p className="mt-2 text-xs text-red-600 text-center">{error}</p>
            )}
        </div>
    );
};

// Sub-component for Standard Pin
const StandardPin: React.FC<{ content: StandardPinContent }> = ({ content }) => (
    <div data-pin-content className="w-full h-full bg-white rounded-2xl flex flex-col items-center justify-between p-4 shadow-lg">
        <h1 className="font-display text-3xl tracking-wide text-slate-800 text-center my-4 capitalize">{content.title}</h1>
        <div className="w-full aspect-[2/3] relative overflow-hidden rounded-2xl">
            <img src={content.image?.startsWith('http') || content.image?.startsWith('data:') ? content.image : `data:image/jpeg;base64,${content.image}`} alt={content.title} className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-center text-center pt-20 pb-10 px-8 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white text-4xl font-bold leading-tight font-display uppercase tracking-widest"
                    dangerouslySetInnerHTML={{ __html: content.description.replace(/\n/g, '<br />') }}>
                </p>
            </div>
        </div>
        <p className="font-sans text-sm tracking-[0.2em] text-gray-500 text-center mt-6">INNERUNIONHOME.COM</p>
    </div>
);

// Sub-component for Listicle Pin
const ListiclePin: React.FC<{ content: ListiclePinContent }> = ({ content }) => {
    const isInfographic = content.style === 'infographic' || !!content.backgroundImage;
    const tokens = content.designTokens;

    if (isInfographic) {
        // Dynamic Styles
        const fontClass = tokens?.fontFamily === 'serif' ? 'font-serif' : tokens?.fontFamily === 'handwritten' ? 'font-handwriting' : 'font-sans';
        const overlayClass = tokens?.overlayStyle === 'solid' ? 'bg-white/95 text-slate-900' : 'bg-gradient-to-t from-black/90 via-black/50 to-transparent text-white';
        const accentColor = tokens?.colorPalette?.accent || '#ec4899'; // Default pink

        return (
            <div className={`relative w-full h-full overflow-hidden flex flex-col ${fontClass}`} style={{ backgroundColor: tokens?.colorPalette?.background || '#fff' }}>
                {content.backgroundImage && (
                    <img
                        src={content.backgroundImage.startsWith('http') || content.backgroundImage.startsWith('data:') ? content.backgroundImage : `data:image/jpeg;base64,${content.backgroundImage}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        alt="Background"
                    />
                )}

                {/* Content Overlay */}
                <div className={`relative z-10 mt-auto p-6 ${overlayClass} overflow-y-auto max-h-full`}>
                    <div className="mb-5">
                        <p className="text-[10px] font-bold tracking-widest uppercase opacity-70 mb-2">{content.title}</p>
                        {content.headline && (
                            <h1 className="text-3xl font-extrabold leading-tight mb-2" style={{ color: tokens?.overlayStyle === 'solid' ? accentColor : 'white' }}>
                                {content.headline}
                            </h1>
                        )}
                        {content.subHeadline && (
                            <p className="text-xs font-medium opacity-90 leading-relaxed max-w-[90%]">{content.subHeadline}</p>
                        )}
                    </div>

                    <div className="space-y-3">
                        {content.tips.map((tip, index) => (
                            <div key={index} className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                    {/* Icon or Checkmark */}
                                    {tokens?.overlayStyle === 'solid' ? (
                                        <div className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: accentColor }}>
                                            {index + 1}
                                        </div>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-emerald-400">
                                            <path splip-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-xs font-bold leading-tight mb-0.5">{tip.subtitle}</h3>
                                    <p className="text-[10px] opacity-80 leading-tight">{tip.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 pt-4 border-t border-current opacity-40 text-center">
                        <p className="text-[9px] font-bold tracking-[0.2em] uppercase">READ THE FULL GUIDE</p>
                    </div>
                </div>
            </div>
        );
    }

    // Legacy Grid Layout
    return (
        <div className="w-full h-full rounded-lg overflow-hidden bg-white p-4 flex flex-col gap-3 shadow-lg justify-between">
            <h2 className="text-center text-2xl font-bold text-gray-800 tracking-tight font-display p-2">{content.title}</h2>
            <div className="grid grid-cols-2 gap-3 flex-grow">
                {content.tips.map((tip, index) => (
                    <div key={index} className="bg-gray-50 rounded-md p-2 flex flex-col items-center text-center border border-gray-200">
                        <div className="w-full aspect-square rounded-sm overflow-hidden mb-2">
                            {tip.illustration && (
                                <img src={tip.illustration.startsWith('http') || tip.illustration.startsWith('data:') ? tip.illustration : `data:image/jpeg;base64,${tip.illustration}`} alt={tip.subtitle} className="w-full h-full object-cover" />
                            )}
                        </div>
                        <h3 className="text-xs font-bold text-gray-800">{tip.subtitle}</h3>
                        <p className="text-[10px] text-gray-600 leading-tight">{tip.description}</p>
                    </div>
                ))}
            </div>
            <p className="font-sans text-sm tracking-[0.2em] text-gray-500 text-center pt-2 mt-2">INNERUNIONHOME.COM</p>
        </div>
    );
};

// Sub-component for Carousel Pin
const CarouselPin: React.FC<{ content: CarouselPinContent }> = ({ content }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [showDescription, setShowDescription] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const totalSlides = content.userImages.length;

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

    if (totalSlides === 0) return null;

    const currentDescription = content.generated.slideDescriptions?.[currentSlide];

    // Function to download all slides as PNG
    const downloadAllSlides = async () => {
        setIsDownloading(true);
        try {
            for (let i = 0; i < totalSlides; i++) {
                await downloadSlideAsPNG(i);
                // Small delay between downloads to avoid overwhelming the browser
                if (i < totalSlides - 1) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }
        } catch (error) {
            console.error('Error downloading slides:', error);
            alert('Failed to download some slides. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    const downloadSlideAsPNG = async (slideIndex: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                // Set canvas dimensions based on aspect ratio
                const aspectRatios: Record<AspectRatio, [number, number]> = {
                    '1:1': [1080, 1080],
                    '2:3': [1080, 1620],
                    '4:5': [1080, 1350],
                    '4:3': [1080, 810]
                };
                const [width, height] = aspectRatios[content.imageAspectRatio] || [1080, 1620];
                canvas.width = width;
                canvas.height = height;

                // Calculate dimensions to cover the entire canvas (object-fit: cover behavior)
                const imgAspect = img.width / img.height;
                const canvasAspect = width / height;

                let drawWidth, drawHeight, offsetX, offsetY;

                if (imgAspect > canvasAspect) {
                    // Image is wider than canvas - fit to height and crop sides
                    drawHeight = height;
                    drawWidth = img.width * (height / img.height);
                    offsetX = (width - drawWidth) / 2;
                    offsetY = 0;
                } else {
                    // Image is taller than canvas - fit to width and crop top/bottom
                    drawWidth = width;
                    drawHeight = img.height * (width / img.width);
                    offsetX = 0;
                    offsetY = (height - drawHeight) / 2;
                }

                // Draw the image to cover the entire canvas
                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

                // Check if we have tips content for this slide
                const tipContent = content.generated.slideTips?.[slideIndex];

                if (tipContent) {
                    // Render tips-style overlay
                    const position = tipContent.position || 'top';

                    // Add gradient overlay based on position
                    const gradient = position === 'bottom'
                        ? ctx.createLinearGradient(0, height * 0.4, 0, height)
                        : position === 'top'
                            ? ctx.createLinearGradient(0, 0, 0, height * 0.6)
                            : ctx.createLinearGradient(0, 0, 0, height);

                    if (position === 'bottom') {
                        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
                        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
                    } else if (position === 'top') {
                        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
                        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    } else {
                        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
                        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.3)');
                    }
                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 0, width, height);

                    // Calculate starting Y based on position
                    const padding = height * 0.06;
                    let currentY = position === 'bottom'
                        ? height - padding - ((tipContent.bodyLines.length + 1) * (height * 0.045))
                        : position === 'top'
                            ? padding
                            : height * 0.35;

                    // Set text properties
                    ctx.textAlign = 'left';
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
                    ctx.shadowBlur = 6;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 2;

                    // Draw title in lime/yellow-green
                    const titleFontSize = Math.floor(width / 18);
                    ctx.font = `bold italic ${titleFontSize}px "Inter", "Helvetica", sans-serif`;
                    ctx.fillStyle = '#CDDC39'; // Lime color
                    ctx.fillText(tipContent.title, padding, currentY);
                    currentY += titleFontSize * 1.5;

                    // Draw body lines in white
                    const bodyFontSize = Math.floor(width / 24);
                    ctx.font = `600 ${bodyFontSize}px "Inter", "Helvetica", sans-serif`;
                    ctx.fillStyle = 'white';

                    tipContent.bodyLines.forEach((line) => {
                        ctx.fillText(line, padding, currentY);
                        currentY += bodyFontSize * 1.4;
                    });

                    // Draw emphasis text in italic if present
                    if (tipContent.emphasisText) {
                        currentY += bodyFontSize * 0.3;
                        ctx.font = `italic ${bodyFontSize}px "Inter", "Helvetica", sans-serif`;
                        ctx.fillStyle = 'white';
                        ctx.fillText(tipContent.emphasisText, padding, currentY);
                    }

                    // Reset shadow
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                } else {
                    // Legacy: Add gradient overlay at bottom
                    const gradient = ctx.createLinearGradient(0, height * 0.6, 0, height);
                    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
                    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)');
                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 0, width, height);

                    // Add text overlay
                    const slideText = content.generated.slideTexts[slideIndex] || '';
                    if (slideText) {
                        const fontSize = Math.floor(width / 18);
                        ctx.font = `900 ${fontSize}px "Playfair Display", "Georgia", "Times New Roman", serif`;
                        ctx.fillStyle = 'white';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                        ctx.shadowBlur = 8;
                        ctx.shadowOffsetX = 0;
                        ctx.shadowOffsetY = 2;

                        const letterSpacing = fontSize * 0.12;
                        const words = slideText.split(' ');
                        const lines: string[] = [];
                        let currentLine = words[0];

                        for (let j = 1; j < words.length; j++) {
                            const testLine = currentLine + ' ' + words[j];
                            const testWidth = measureTextWithSpacing(ctx, testLine, letterSpacing);
                            if (testWidth > width * 0.9) {
                                lines.push(currentLine);
                                currentLine = words[j];
                            } else {
                                currentLine = testLine;
                            }
                        }
                        lines.push(currentLine);

                        const lineHeight = fontSize * 1.2;
                        const totalTextHeight = lines.length * lineHeight;
                        const startY = height - totalTextHeight - (height * 0.08);

                        lines.forEach((line, index) => {
                            drawTextWithSpacing(ctx, line, width / 2, startY + (index * lineHeight), letterSpacing);
                        });

                        ctx.shadowColor = 'transparent';
                        ctx.shadowBlur = 0;
                    }
                }

                // Helper function to measure text width with letter spacing
                function measureTextWithSpacing(ctx: CanvasRenderingContext2D, text: string, spacing: number): number {
                    const chars = text.split('');
                    let totalWidth = 0;
                    chars.forEach((char, i) => {
                        totalWidth += ctx.measureText(char).width;
                        if (i < chars.length - 1) totalWidth += spacing;
                    });
                    return totalWidth;
                }

                // Helper function to draw text with letter spacing
                function drawTextWithSpacing(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, spacing: number): void {
                    const chars = text.split('');
                    const totalWidth = measureTextWithSpacing(ctx, text, spacing);
                    let currentX = x - totalWidth / 2;

                    chars.forEach((char) => {
                        ctx.fillText(char, currentX, y);
                        currentX += ctx.measureText(char).width + spacing;
                    });
                }

                // Convert to data URL and download (more reliable across browsers)
                const dataUrl = canvas.toDataURL('image/png', 1.0);
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = `carousel-slide-${slideIndex + 1}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                resolve();
            };

            img.onerror = () => reject(new Error(`Failed to load image ${slideIndex + 1}`));
            img.src = content.userImages[slideIndex];
        });
    };

    return (
        <div className="w-full h-full bg-white rounded-2xl flex flex-col items-center justify-between p-4 shadow-lg">
            <h1 className="font-display text-3xl tracking-wide text-slate-800 text-center my-4 capitalize">{content.title}</h1>
            <div className={`w-full ${getAspectRatioClass(content.imageAspectRatio)} relative overflow-hidden rounded-2xl group`}>
                {/* Slide Content */}
                <img src={content.userImages[currentSlide]} alt={`Slide ${currentSlide + 1}`} className="w-full h-full object-cover" />

                {/* Tips-style Text Overlay */}
                {content.generated.slideTips && content.generated.slideTips[currentSlide] ? (
                    <div
                        className={`absolute inset-0 flex flex-col px-6 py-8 ${content.generated.slideTips[currentSlide].position === 'top' ? 'justify-start' :
                            content.generated.slideTips[currentSlide].position === 'center' ? 'justify-center' :
                                'justify-end'
                            }`}
                        style={{
                            background: content.generated.slideTips[currentSlide].position === 'bottom'
                                ? 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                : content.generated.slideTips[currentSlide].position === 'top'
                                    ? 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
                                    : 'rgba(0,0,0,0.3)'
                        }}
                    >
                        {/* Title - Lime/Yellow-Green Accent */}
                        <h3
                            className="text-xl md:text-2xl font-bold italic mb-3"
                            style={{ color: '#CDDC39', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                        >
                            {content.generated.slideTips[currentSlide].title}
                        </h3>

                        {/* Body Lines - White */}
                        <div className="space-y-1.5">
                            {content.generated.slideTips[currentSlide].bodyLines.map((line, idx) => (
                                <p
                                    key={idx}
                                    className="text-white text-base md:text-lg font-semibold leading-tight"
                                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                                >
                                    {line}
                                </p>
                            ))}
                        </div>

                        {/* Emphasis Text - Italic */}
                        {content.generated.slideTips[currentSlide].emphasisText && (
                            <p
                                className="text-white text-base md:text-lg italic mt-4"
                                style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                            >
                                {content.generated.slideTips[currentSlide].emphasisText}
                            </p>
                        )}
                    </div>
                ) : (
                    /* Legacy: Simple text overlay at bottom */
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-center text-center pt-20 pb-10 px-8 bg-gradient-to-t from-black/70 to-transparent">
                        <p className="text-white text-4xl font-bold leading-tight font-display uppercase tracking-widest"
                            dangerouslySetInnerHTML={{ __html: content.generated.slideTexts[currentSlide]?.replace(/\n/g, '<br />') || '' }}>
                        </p>
                    </div>
                )}

                {/* Info Button - Shows description */}
                {currentDescription && (
                    <button
                        onClick={() => setShowDescription(!showDescription)}
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full transition-all z-10"
                        aria-label="Toggle description"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                        </svg>
                    </button>
                )}

                {/* Description Overlay */}
                {showDescription && currentDescription && (
                    <div className="absolute inset-0 bg-black/90 flex items-center justify-center p-6 z-20">
                        <div className="text-white text-center max-w-md">
                            <h3 className="text-lg font-bold mb-3 font-display">Slide {currentSlide + 1} Details</h3>
                            <p className="text-sm leading-relaxed">{currentDescription}</p>
                            <button
                                onClick={() => setShowDescription(false)}
                                className="mt-4 px-4 py-2 bg-white text-gray-800 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

                {/* Navigation */}
                {totalSlides > 1 && (
                    <>
                        <button onClick={prevSlide} className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/70 text-gray-800 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10" aria-label="Previous slide">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                        </button>
                        <button onClick={nextSlide} className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/70 text-gray-800 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10" aria-label="Next slide">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                        </button>
                    </>
                )}
                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {Array.from({ length: totalSlides }).map((_, i) => (
                        <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentSlide === i ? 'bg-white scale-110' : 'bg-white/60'}`} aria-current={currentSlide === i}></div>
                    ))}
                </div>
            </div>
            {/* Description below the image */}
            {currentDescription && (
                <div className="w-full mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600 leading-relaxed text-center">
                        <span className="font-semibold text-slate-800">Slide {currentSlide + 1}: </span>
                        {currentDescription}
                    </p>
                </div>
            )}

            {/* Download Button */}
            <button
                onClick={downloadAllSlides}
                disabled={isDownloading}
                className="w-full mt-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg"
            >
                {isDownloading ? (
                    <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Downloading {totalSlides} slides...</span>
                    </>
                ) : (
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                        </svg>
                        <span>Download All {totalSlides} Slides as PNG</span>
                    </>
                )}
            </button>

            <p className="font-sans text-sm tracking-[0.2em] text-gray-500 text-center mt-4">INNERUNIONHOME.COM</p>
        </div>
    );
};


// Sub-component for Theme Collage Pin
const ThemeCollagePin: React.FC<{ content: ThemeCollagePinContent }> = ({ content }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const totalSlides = 1 + content.userImageGroups.length; // 1 cover slide + image slides

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

    const aspectRatioClass = getAspectRatioClass(content.imageAspectRatio);

    const renderSlide = () => {
        if (currentSlide === 0) { // Cover slide
            return (
                <div className="w-full h-full bg-neutral-100 text-neutral-800 flex flex-col justify-center items-center text-center p-8 font-serif">
                    <p className="text-sm font-semibold tracking-widest text-neutral-600">{content.generated.text.preTitle}</p>
                    <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter my-2 font-display">{content.generated.text.title}</h2>
                    <p className="text-sm text-neutral-700 max-w-xs">{content.generated.text.description}</p>
                </div>
            )
        }

        const imageIndex = currentSlide - 1;
        const imageGroup = content.userImageGroups[imageIndex];

        if (!imageGroup || imageGroup.length === 0) {
            return <div className="w-full h-full bg-gray-200" aria-label="Empty slide placeholder" />;
        }

        const imgClass = "w-full h-full object-cover";
        const altText = (index: number) => `Collage image ${index + 1} for slide ${currentSlide}`;

        switch (imageGroup.length) {
            case 1:
                return (
                    <div className="w-full h-full bg-gray-200">
                        <img src={imageGroup[0]} alt={altText(0)} className={imgClass} />
                    </div>
                );
            case 2:
                return (
                    <div className="w-full h-full flex flex-row">
                        <div className="w-1/2 h-full"><img src={imageGroup[0]} alt={altText(0)} className={imgClass} /></div>
                        <div className="w-1/2 h-full"><img src={imageGroup[1]} alt={altText(1)} className={imgClass} /></div>
                    </div>
                );
            case 3:
                return (
                    <div className="w-full h-full flex flex-row">
                        <div className="w-1/2 h-full"><img src={imageGroup[0]} alt={altText(0)} className={imgClass} /></div>
                        <div className="w-1/2 h-full flex flex-col">
                            <div className="h-1/2 w-full"><img src={imageGroup[1]} alt={altText(1)} className={imgClass} /></div>
                            <div className="h-1/2 w-full"><img src={imageGroup[2]} alt={altText(2)} className={imgClass} /></div>
                        </div>
                    </div>
                );
            default: // Handles 4 or more, but we chunk by 4
                return (
                    <div className="w-full h-full grid grid-cols-2 grid-rows-2">
                        {imageGroup.slice(0, 4).map((imgSrc, index) => (
                            <div key={index} className="col-span-1 row-span-1">
                                <img src={imgSrc} alt={altText(index)} className={imgClass} />
                            </div>
                        ))}
                    </div>
                );
        }
    };

    return (
        <div className="w-full flex flex-col items-center justify-center gap-4">
            <div className={`w-full ${aspectRatioClass} rounded-lg overflow-hidden relative shadow-lg group bg-white`}>
                {/* Slide Content */}
                <div className="w-full h-full">
                    {renderSlide()}
                </div>

                {/* Navigation */}
                {totalSlides > 1 && (
                    <>
                        <button onClick={prevSlide} className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/70 text-gray-800 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10" aria-label="Previous slide">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                        </button>
                        <button onClick={nextSlide} className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/70 text-gray-800 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10" aria-label="Next slide">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                        </button>
                    </>
                )}

                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {Array.from({ length: totalSlides }).map((_, i) => {
                        const isActive = currentSlide === i;
                        const onCover = currentSlide === 0;
                        let dotColor = '';
                        if (onCover) {
                            dotColor = isActive ? 'bg-neutral-800' : 'bg-neutral-400';
                        } else {
                            dotColor = isActive ? 'bg-white' : 'bg-white/50';
                        }
                        return <div key={i} className={`w-2 h-2 rounded-full transition-colors ${dotColor}`} aria-current={isActive}></div>
                    })}
                </div>
            </div>
            <p className="font-sans text-sm tracking-[0.2em] text-gray-500 text-center">INNERUNIONHOME.COM</p>
        </div>
    )
};

// Sub-component for Tip Pin
const TipPin: React.FC<{ content: TipPinContent }> = ({ content }) => {
    return (
        <div className="w-full h-full bg-white rounded-2xl flex flex-col items-center justify-between p-4 shadow-lg">
            <h1 className="font-display text-3xl tracking-wide text-slate-800 text-center my-4 capitalize">{content.generated.title}</h1>
            <div className={`w-full ${getAspectRatioClass(content.imageAspectRatio)} relative overflow-hidden rounded-2xl`}>
                <img src={content.userImage} alt={content.generated.title} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 pt-24 pb-8 px-8 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                    <p className="text-white text-lg leading-relaxed font-sans">
                        {content.generated.tipText}
                    </p>
                </div>
            </div>
            <p className="font-sans text-sm tracking-[0.2em] text-gray-500 text-center mt-6">INNERUNIONHOME.COM</p>
        </div>
    );
};

// Sub-component for Smart Carousel Pin - HTML text overlay with consistent design
const SmartCarouselPin: React.FC<{ content: SmartCarouselPinContent }> = ({ content }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const slideRef = useRef<HTMLDivElement>(null);
    const totalSlides = content.slides.length;

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

    const currentSlideData = content.slides[currentSlide];
    const palette = content.colorPalette;

    // Download single slide using html2canvas
    const downloadCurrentSlide = async () => {
        if (!slideRef.current) return;

        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(slideRef.current, {
                useCORS: true,
                allowTaint: true,
                scale: 2,
                backgroundColor: null,
                logging: false
            });

            const dataUrl = canvas.toDataURL('image/png', 1.0);
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `carousel-slide-${currentSlide + 1}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    // Download all slides as PNG using html2canvas
    const downloadAllSlides = async () => {
        if (!slideRef.current) return;

        setIsDownloading(true);
        const html2canvas = (await import('html2canvas')).default;

        try {
            for (let i = 0; i < totalSlides; i++) {
                setCurrentSlide(i);
                // Wait for React to update the DOM
                await new Promise(resolve => setTimeout(resolve, 200));

                const canvas = await html2canvas(slideRef.current, {
                    useCORS: true,
                    allowTaint: true,
                    scale: 2,
                    backgroundColor: null,
                    logging: false
                });

                const dataUrl = canvas.toDataURL('image/png', 1.0);
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = `carousel-slide-${i + 1}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Small delay between downloads
                if (i < totalSlides - 1) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }
        } catch (error) {
            console.error('Error downloading slides:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="w-full h-full bg-white rounded-2xl flex flex-col items-center justify-between p-4 shadow-lg">
            <h1 className="font-display text-2xl tracking-wide text-slate-800 text-center my-3 capitalize">{content.mainTitle}</h1>

            <div ref={slideRef} className={`w-full ${getAspectRatioClass(content.imageAspectRatio)} relative overflow-hidden rounded-2xl group`}>
                {/* Image */}
                {currentSlideData.image ? (
                    <img
                        src={currentSlideData.image}
                        alt={currentSlideData.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                        <span className="text-slate-400">No image</span>
                    </div>
                )}

                {/* Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: `linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)`
                    }}
                />

                {/* Text Content - HTML Overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col justify-end">
                    {/* Type Badge */}
                    {currentSlideData.type === 'hook' && (
                        <span
                            className="inline-block px-2 py-1 rounded text-xs font-bold uppercase mb-2 w-fit"
                            style={{ backgroundColor: palette.accent, color: 'white' }}
                        >
                            {content.slides.length - 2} Tips
                        </span>
                    )}

                    {/* Tip Number */}
                    {currentSlideData.tipNumber && (
                        <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mb-2"
                            style={{ backgroundColor: palette.accent }}
                        >
                            {currentSlideData.tipNumber}
                        </div>
                    )}

                    {/* Title - Accent Color */}
                    <h3
                        className="text-xl md:text-2xl font-bold mb-2"
                        style={{
                            color: palette.accent,
                            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                            fontFamily: 'Inter, sans-serif'
                        }}
                    >
                        {currentSlideData.title}
                    </h3>

                    {/* Subtitle (for hook) */}
                    {currentSlideData.subtitle && (
                        <p
                            className="text-white text-sm mb-2"
                            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                        >
                            {currentSlideData.subtitle}
                        </p>
                    )}

                    {/* Body Text - White */}
                    <p
                        className="text-white text-sm md:text-base leading-relaxed"
                        style={{
                            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                            fontFamily: 'Inter, sans-serif'
                        }}
                    >
                        {currentSlideData.bodyText}
                    </p>
                </div>

                {/* Branding URL */}
                {content.brandingUrl && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
                        <span
                            className="text-xs font-semibold uppercase tracking-widest"
                            style={{ color: 'rgba(255,255,255,0.6)' }}
                        >
                            {content.brandingUrl}
                        </span>
                    </div>
                )}

                {/* Navigation */}
                {totalSlides > 1 && (
                    <>
                        <button onClick={prevSlide} className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/70 text-gray-800 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                        </button>
                        <button onClick={nextSlide} className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/70 text-gray-800 hover:bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                        </button>
                    </>
                )}

                {/* Dots Indicator */}
                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {content.slides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentSlide(i)}
                            className={`w-2 h-2 rounded-full transition-all ${currentSlide === i ? 'bg-white scale-125' : 'bg-white/50'}`}
                        />
                    ))}
                </div>
            </div>

            {/* Slide Type Indicator */}
            <div className="w-full mt-3 flex justify-center gap-2">
                {content.slides.map((slide, i) => (
                    <button
                        key={i}
                        onClick={() => setCurrentSlide(i)}
                        className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${currentSlide === i
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                    >
                        {slide.type === 'hook' ? '🎯' : slide.type === 'cta' ? '📢' : `#${slide.tipNumber}`}
                    </button>
                ))}
            </div>

            {/* Download Button */}
            <button
                onClick={downloadAllSlides}
                disabled={isDownloading}
                className="w-full mt-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 disabled:opacity-50 text-white font-bold py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
            >
                {isDownloading ? (
                    <>
                        <Loader />
                        <span>Downloading...</span>
                    </>
                ) : (
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                        </svg>
                        <span>Download All {totalSlides} Slides</span>
                    </>
                )}
            </button>
        </div>
    );
};

interface PinPreviewProps {
    isLoading: boolean;
    content: AnyPinContent | null;
    selectedIcon: Icon;
    selectedPalette: ColorPalette;
}

const PinPreview: React.FC<PinPreviewProps> = ({ isLoading, content, selectedIcon, selectedPalette }) => {
    const pinContainerRef = useRef<HTMLDivElement>(null);

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center text-center p-8 bg-white/80 rounded-lg">
                    <Loader className="w-12 h-12 text-pink-500" />
                    <p className="mt-4 text-lg font-semibold text-slate-700">Generating your pin...</p>
                    <p className="mt-2 text-sm text-slate-500">This might take a moment, especially for images.</p>
                </div>
            );
        }

        if (!content) {
            return (
                <div className="w-full h-full flex items-center justify-center text-center p-8 bg-white/80 rounded-lg">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-10">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        <h3 className="mt-4 text-lg font-medium text-slate-700">Your pin preview will appear here</h3>
                        <p className="mt-1 text-sm text-slate-500">Fill out the form and click "Generate Pin".</p>
                    </div>
                </div>
            );
        }

        switch (content.type) {
            case 'standard':
                return <StandardPin content={content} />;
            case 'listicle':
                return <ListiclePin content={content} />;
            case 'carousel':
                return <CarouselPin content={content} />;
            case 'theme-collage':
                return <ThemeCollagePin content={content} />;
            case 'tip':
                return <TipPin content={content} />;
            case 'smart-carousel':
                return <SmartCarouselPin content={content} />;
            default:
                return <p>Unknown pin type.</p>;
        }
    };

    return (
        <div className="w-full lg:w-1/2 flex flex-col">
            <div className="w-full lg:aspect-[2/3] max-w-[450px] mx-auto flex items-center justify-center p-4 bg-slate-200 rounded-2xl border border-slate-300/80 min-h-[550px] lg:min-h-0">
                <div ref={pinContainerRef} className="w-full h-full flex items-center justify-center">
                    {renderContent()}
                </div>
            </div>

            {content && !isLoading && (
                <div className="mt-4 flex justify-center">
                    <button
                        onClick={async () => {
                            if (!confirm('Schedule this pin to your Posting Calendar?')) return;
                            try {
                                const res = await fetch(`${API_BASE}/api/calendar/posts/add`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        platform: 'pinterest',
                                        content: {
                                            topic: content.type === 'tip' ? content.generated.title : (content as any).title || 'New Pin',
                                            caption: 'Generated by Pin Generator',
                                            hashtags: ['pinterest', 'design']
                                        },
                                        type: content.type === 'carousel' ? 'carousel_pin' : 'standard_pin'
                                    })
                                });
                                const data = await res.json();
                                if (data.success) {
                                    alert('✅ Pin scheduled successfully!');
                                } else {
                                    alert('❌ Failed to schedule: ' + data.error);
                                }
                            } catch (e) {
                                alert('❌ Error scheduling pin');
                            }
                        }}
                        className="px-6 py-3 bg-red-600 text-white font-bold rounded-lg shadow hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                        <span>📅 Schedule to Calendar</span>
                    </button>
                </div>
            )}

            {/* Export Panel - for Standard, Listicle, Tip pins (Carousel and Smart Carousel have their own) */}
            {content && !isLoading && content.type !== 'carousel' && content.type !== 'smart-carousel' && (
                <div className="max-w-[450px] mx-auto">
                    <ExportPanel
                        containerRef={pinContainerRef}
                        filename={`pin-${content.type}`}
                    />
                </div>
            )}
        </div>
    );
};

export default PinPreview;